import { useEffect, useRef, useState } from 'react';
import { EMPTY_RULE, NOTIFY_LINE, attachmentText, summarizeRule } from './mailRuleSummary';
import MailNotifyErrorNotice from './MailNotifyErrorNotice';
import MailRuleSenderList from './MailRuleSenderList';
import MailRuleClearInput from './MailRuleClearInput';
import { ChevronDownIcon, CloseIcon, MinusIcon } from './mailRuleIcons';
import { InfoIcon } from './mailNotifyIcons';
import './MailRuleForm.css';

const AI_MAX = 500;

/**
 * AGUI-1285（10-27 手機重測）：Android 鍵盤組字中（輸入法上方有候選字列）不會套用輸入框的 maxLength，
 * 手機上可以打超過上限（例如 AI 描述 501 / 500），網頁版則打不進去。maxLength 照留，輸入時再自己截掉超過的部分
 */
const clip = (value, max) => (value.length > max ? value.slice(0, max) : value);

/**
 * 欄位標籤＋ⓘ說明（比照頁面標題旁的「使用說明」圖示）：說明文字平常收起，按 ⓘ 從圖示下方長出說明框，
 * 再按一次、按 Esc 或點框外收起。說明框收起時仍留在 DOM（`hidden`），輸入框的 aria-describedby 照樣讀得到。
 */
function LabelWithHint({ htmlFor, label, hint, hintId }) {
  const [open, setOpen] = useState(false);
  const [arrowX, setArrowX] = useState(0);
  const rowRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (!rowRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [open]);

  const toggle = () => {
    const b = buttonRef.current;
    // 說明框和欄位同寬，小箭頭對準 ⓘ（ⓘ 跟在標籤字後面，位置隨標籤長度變）
    if (b) setArrowX(b.offsetLeft + b.offsetWidth / 2);
    setOpen((v) => !v);
  };

  return (
    <div ref={rowRef} className="mail-rule-form__label-row"
      onKeyDown={(e) => {
        if (open && e.key === 'Escape') {
          e.stopPropagation();
          setOpen(false);
          buttonRef.current?.focus();
        }
      }}>
      <label htmlFor={htmlFor}>{label}</label>
      <button type="button" ref={buttonRef} className="mail-rule-form__info"
        aria-label={`${label}說明`} title="說明" aria-expanded={open} aria-controls={hintId}
        onClick={toggle}>
        <InfoIcon size={16} />
      </button>
      <div id={hintId} className="mail-rule-form__hint-pop" hidden={!open}
        style={{ '--mail-rule-hint-arrow-x': `${arrowX}px` }}>
        {hint}
      </div>
    </div>
  );
}

/**
 * 主旨關鍵字清單（10-19 PO 檢視，取代原本的標籤輸入）：版面比照 {@link MailRuleSenderList}——
 * 上面是「輸入框＋加入」（Enter 也能加入），下面一列一個關鍵字、各自有 ✕，字比原本的小標籤大。
 * 只有按「加入」或 Enter 才加入（D-1022-06）：離開輸入框、打逗號都不加入，逗號當一般字元。
 *
 * `max` 對齊後端 MailRuleValidator 的 MAX_ITEMS(20)（design.md §5.1/§10），`maxLength` 對齊 MAX_KEYWORD(50)。
 * 前後端驗證規則必須一致：達到 `max` 個時忽略新增並顯示提示，避免使用者填了送出才被 400 打回。
 *
 * 輸入框的字（`text`）與錯誤（`error`）由表單保管（10-27 使用者裁決 A）：「過濾規則」收起時這個元件不掛載，
 * 字放在元件裡會跟著消失；放在表單才能在收起後保留，按「儲存」時也查得到
 */
function KeywordList({
  id, label, values, onChange, placeholder, hint, max = 20, maxLength = 50,
  text, onTextChange: setText, error, onErrorChange: setError,
}) {
  // 新加入的那列淡入（樣式見 .mail-rule-senders__item--new），和寄件者清單同一個效果（10-19 PO 檢視：兩邊加入時要一致）；
  // 1.2 秒後拿掉 class，下次加入同一個字時動畫才會再跑
  const [justAdded, setJustAdded] = useState(null);
  const inputRef = useRef(null);
  const atMax = values.length >= max;

  useEffect(() => {
    if (!justAdded) return undefined;
    const timer = setTimeout(() => setJustAdded(null), 1200);
    return () => clearTimeout(timer);
  }, [justAdded]);

  /** @returns {string[]|null} 加入後的清單；沒加入時為 null */
  const add = () => {
    const v = text.trim();
    if (!v) return null;
    if (atMax) return null;
    if (values.includes(v)) {
      setError(`「${v}」已在清單中`);
      return null;
    }
    const next = [...values, v];
    onChange(next);
    setJustAdded(v);
    setText('');
    setError(null);
    return next;
  };

  const errorId = error ? `${id}-error` : undefined;
  const hintId = hint ? `${id}-hint` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className="mail-rule-form__field">
      {hint
        ? <LabelWithHint htmlFor={id} label={label} hint={hint} hintId={hintId} />
        : <label htmlFor={id}>{label}</label>}
      <div className="mail-rule-senders__add">
        <MailRuleClearInput label={label} value={text} inputRef={inputRef}
          onClear={() => {
            setText('');
            setError(null);
          }}>
          <input
            id={id}
            ref={inputRef}
            value={text}
            maxLength={maxLength}
            placeholder={placeholder}
            className={error ? 'mail-rule-form__input--invalid' : undefined}
            aria-invalid={Boolean(error)}
            aria-describedby={describedBy}
            onChange={(e) => {
              setText(clip(e.target.value, maxLength));
              setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                add();
              }
            }}
          />
        </MailRuleClearInput>
        <button type="button" className="mail-rule-senders__add-button" disabled={atMax} onClick={add}>
          加入
        </button>
      </div>
      {error && <div id={errorId} className="mail-rule-form__field-error" role="alert">{error}</div>}
      {/* 10-19 使用者裁決：沒有關鍵字時不放「尚未加入」虛線框 */}
      {values.length > 0 && (
        <ul className="mail-rule-senders__list" aria-label={`已加入的${label}`}>
          {values.map((v) => (
            <li key={v} className={`mail-rule-senders__item${justAdded === v ? ' mail-rule-senders__item--new' : ''}`}>
              <span className="mail-rule-senders__value">{v}</span>
              <button type="button" className="mail-rule-senders__remove" aria-label={`移除 ${v}`}
                onClick={() => onChange(values.filter((x) => x !== v))}>
                <CloseIcon size={16} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {atMax && <div className="mail-rule-form__hint">最多 {max} 個</div>}
    </div>
  );
}

/**
 * 有「不限」（空值）選項的下拉（10-19 PO 檢視）：
 * - 不限時字是灰的、選了值是深色，一眼看得出有沒有設定
 * - 有值時框內右側（箭頭左邊）出現 ✕，按了回到「不限」
 * - 箭頭自己畫、往左內縮，和 ✕ 同色（見 MailRuleForm.css 的 .mail-rule-form__select）
 */
function ClearableSelect({ id, label, value, onChange, children }) {
  const empty = value === '';
  return (
    <div className="mail-rule-form__field">
      <label htmlFor={id}>{label}</label>
      {/* 比照 Element UI 的可清除下拉：右側只有一個圖示位置，有值時滑過（或鍵盤焦點在裡面）箭頭換成 ✕ */}
      <span className={`mail-rule-form__select mail-rule-form__select--clearable${empty ? '' : ' mail-rule-form__select--has-value'}`}>
        <select id={id} value={value} className={empty ? 'mail-rule-form__select-input--empty' : undefined}
          onChange={(e) => onChange(e.target.value)}>
          {children}
        </select>
        {!empty && (
          <button type="button" className="mail-rule-form__select-clear" aria-label={`清除${label}`} title="清除"
            onClick={() => {
              onChange('');
              document.getElementById(id)?.focus();
            }}>
            <CloseIcon size={16} />
          </button>
        )}
        <ChevronDownIcon size={16} className="mail-rule-form__select-arrow" />
      </span>
    </div>
  );
}

/**
 * 群組外框（10-19 PO 檢視）：標題放在外框<b>上方</b>，和一般欄位的 label 一樣，不再壓在框線上（原本是 fieldset legend）。
 * 寄件者條件與展開後的選填群組共用這一個版面，框樣式、標題位置、內距與間距一致。
 * 可及性：外層 role="group"，以標題 id 當名稱（aria-labelledby），螢幕閱讀器的行為和 fieldset 相同。
 * `aside` 放在標題列右側（選填群組的「−」收起鈕）。
 * `plain`：不畫外框（AGUI-1256：寄件者條件拿掉外框，輸入框與錯誤訊息和規則名稱靠左對齊）。
 */
function FormGroup({ id, title, required = false, aside, bodyId, plain = false, className = '', children }) {
  const titleId = `${id}-title`;
  return (
    <div role="group" aria-labelledby={titleId} className={`mail-rule-form__group ${className}`.trim()}>
      <div id={`${id}-head`} className="mail-rule-form__group-head">
        <span>
          <span id={titleId} className={`mail-rule-form__group-title${required ? ' mail-rule-form__label--required' : ''}`}>
            {title}
          </span>
        </span>
        {aside}
      </div>
      <div id={bodyId} className={plain ? 'mail-rule-form__group-plain' : 'mail-rule-form__group-box'}>{children}</div>
    </div>
  );
}

/**
 * 選填群組（過濾規則、AI 判讀信件），10-06 改為方案 A：
 * - 收起：虛線的「＋ {標題}」按鈕，和範本面板的「＋ 自己設定條件」同一種樣式；
 *   下方小字說明裡面能設什麼，有值時改列目前的設定（`filled`），免得使用者以為是空的
 * - 展開：和寄件者條件同一個 {@link FormGroup} 版面，標題列右側放「−」收起鈕
 *   （10-07 由文字「收起」改為圖示，和收起時的「＋」成對；可及名稱仍是「收起{標題}」）
 * 不用 ▸／▾：字元太小（實測 11px 時只有 5×3～4px）、兩個字元大小不一切換會跳，各平台字型也不同。
 * 收起來時欄位不掛載，但草稿裡的值保留，下方的條件複述照樣列出。
 * 10-19 PO 檢視：展開時標題旁不再標「選填」；`desc` 可省略（收起時只顯示標題）。
 */
function OptionalGroup({ id, title, desc, filled, open, onToggle, className = '', children }) {
  if (!open) {
    return (
      // 10-19 PO 檢視：收起時「有設定」與「還沒填」要一眼分得出來——有設定改實線淡藍底、標「已設定」、內容用一般字色
      <button type="button" className={`mail-rule-form__add-group${filled ? ' mail-rule-form__add-group--filled' : ''}`}
        aria-expanded={false} aria-controls={id} onClick={onToggle}>
        <span className="mail-rule-form__add-group-title">
          {filled ? title : `＋ ${title}`}
          {filled && <span className="mail-rule-form__add-group-chip">已設定</span>}
        </span>
        {(filled || desc) && <span className="mail-rule-form__add-group-desc">{filled ? `目前：${filled}` : desc}</span>}
      </button>
    );
  }
  return (
    <FormGroup id={`${id}-group`} bodyId={id} title={title} className={className}
      aside={
        <button type="button" className="mail-rule-form__group-collapse" aria-expanded aria-controls={id}
          aria-label={`收起${title}`} title="收起" onClick={onToggle}>
          <MinusIcon size={16} />
        </button>
      }>
      {children}
    </FormGroup>
  );
}

const IMPORTANCE_LABEL = { high: '高', normal: '一般', low: '低' };

/** 收起時虛線按鈕上的「目前設定」：過濾規則列出有值的條件，沒有就回空字串（改顯示說明） */
const filterFilled = (d) =>
  [
    d.subjectKeywords.length ? `主旨包含${d.subjectKeywords.map((k) => `「${k}」`).join('或')}` : '',
    d.importance ? `重要性「${IMPORTANCE_LABEL[d.importance] || d.importance}」` : '',
    attachmentText(d.hasAttachment),
  ].filter(Boolean).join('、');

/** 附件下拉的值 ↔ 三態：'' 不限（null）、'yes' 有附件（true）、'no' 沒有附件（false） */
const ATTACHMENT_VALUE = { true: 'yes', false: 'no' };

const scrollToField = (fieldId, block = 'center') =>
  document.getElementById(fieldId)?.scrollIntoView?.({ behavior: 'smooth', block });
const fromAttachmentValue = (value) => (value === 'yes' ? true : value === 'no' ? false : null);

/**
 * 新增規則（AGUI-1113），一步式：表單下方即時顯示結構化複述，按一次「儲存」。
 *
 * A10 起改為<b>在規則面板內展開</b>，不再是 overlay dialog——overlay 會蓋住右欄，
 * 使用者填到一半想確認「我已經有哪幾條規則」就得先關掉草稿。
 *
 * ⛔ 只有新增模式（決議 #6）：本期不做規則修改，要改就刪掉重建。
 * `initial` 只用來預填草稿（範本、測試），不代表在改既有規則。
 * `focusId` 是打開後游標要停的欄位 id，由範本帶入；
 * `senderMode` 是寄件者輸入列一開始的類型（例如「特定公司來信」直接是「整個網域」）。
 *
 * 版面固定（10-06 使用者裁決）：不論從哪個範本或空白打開，欄位與順序都一樣，範本只預填內容。
 * 必填（名稱、寄件者）永遠展開；選填群組<b>只在打開表單時</b>依「有沒有值」決定展開與否，之後由使用者自己開關，
 * 不會因為刪掉最後一個值就自己收起來——操作中的東西突然不見只會嚇到人。
 * 10-19 PO 檢視：拿掉所有藍色步驟引導；「儲存」不因沒填而灰掉，按了才以紅字標出缺項，只在送出中鎖住防止重複送出。
 *
 * 送出由頁面傳入的 `submit` 處理（SA v2.0 C12）：走 useMailNotify（帶 rulesVersion、處理 202 與 409），
 * 回 `{ ok, data?, error?, timedOut? }`。`busy`＝處理中，儲存先鎖住。
 * AGUI-1246 舊架構下線後只剩這條路徑：不顯示 AI 每日上限說明（NQ-02）、信箱上限 254。
 */
export default function MailRuleForm({ initial, focusId, senderMode, onCancel, onSaved, submit, busy = false, isMobile = false }) {
  const [draft, setDraft] = useState({
    ...EMPTY_RULE,
    ...(initial || {}),
    fromAddresses: initial?.fromAddresses || [],
    fromDomains: initial?.fromDomains || [],
    subjectKeywords: initial?.subjectKeywords || [],
    importance: initial?.importance || '',
    hasAttachment: typeof initial?.hasAttachment === 'boolean' ? initial.hasAttachment : null,
    aiPrompt: initial?.aiPrompt || '',
  });
  // 有預填值的選填群組打開時就展開。只在打開表單時判斷一次，之後改值不影響
  const [filterOpen, setFilterOpen] = useState(() =>
    Boolean(initial?.subjectKeywords?.length || initial?.importance || typeof initial?.hasAttachment === 'boolean'));
  const aiRef = useRef(null);
  const [aiOpen, setAiOpen] = useState(() => Boolean(initial?.aiPrompt));
  const [saving, setSaving] = useState(false);
  // 收到 202 之後：按鈕從「儲存中...」換成「處理中...」，等輪詢有結果才關表單（情境 L）
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);
  // 按過「儲存」才把沒填的必填欄標紅，一打開表單就整片紅字只會嚇到人
  const [submitted, setSubmitted] = useState(false);
  // 10-27 使用者裁決：按「儲存」時用這個檢查寄件者輸入框裡有沒有還沒加入的字
  const senderPendingRef = useRef(null);
  // 關鍵字輸入框的字與錯誤放在表單（10-27 使用者裁決 A）：收起「過濾規則」時保留，按「儲存」時查得到
  const [keywordText, setKeywordText] = useState('');
  const [keywordError, setKeywordError] = useState(null);
  // 按「儲存」被擋下時要捲到的欄位；關鍵字可能要先展開「過濾規則」，所以等畫面更新後再捲
  const [scrollTarget, setScrollTarget] = useState(null);
  const nameRef = useRef(null);
  const formRef = useRef(null);

  useEffect(() => {
    if (focusId) {
      document.getElementById(focusId)?.focus();
      return;
    }
    // AGUI-1340：手機版按「我的規則」的「＋」，表單開在規則清單上方，瀏覽器為了讓「＋」留在原位，
    // 畫面停在表單下半段（只看得到寄件者以下）。打開時捲到表單開頭；不自動聚焦，免得跳出鍵盤蓋住表單
    if (isMobile) formRef.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    // 只在打開表單時捲一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusId]);

  const set = (key) => (value) => setDraft((d) => ({ ...d, [key]: value }));
  // 通知方式那行另外放在最後、前面空一行（10-19），所以先從條件裡拿掉
  const conditionLines = summarizeRule(draft).filter((line) => line !== NOTIFY_LINE);
  const nameMissing = !draft.ruleName.trim();
  const nameInvalid = submitted && nameMissing;
  // 寄件者必填（AGUI-1113）：清單裡至少一位（信箱或網域），輸入框裡還沒加入的字不算
  const senderMissing = draft.fromAddresses.length === 0 && draft.fromDomains.length === 0;
  const senderInvalid = submitted && senderMissing;
  // 10-19：沒填也可以按「儲存」，按了才標紅；只有送出中（儲存中、202 處理中、頁面忙碌）鎖住，防止重複送出
  const inFlight = saving || busy;
  const title = '新增規則';

  useEffect(() => {
    if (scrollTarget) scrollToField(scrollTarget.id, scrollTarget.block);
  }, [scrollTarget]);

  // AGUI-1345：展開選填群組時，原本的「＋」按鈕換成整個群組，瀏覽器為了讓下方內容留在原位會把畫面往下帶，
  // 群組標題跑到頁首後面、只看得到半個群組。展開後把標題捲回畫面；標題本來就看得到（'nearest'）就不動
  const toggleGroup = (open, setOpen, headId) => () => {
    if (!open) setScrollTarget({ id: headId, block: 'nearest' });
    setOpen(!open);
  };

  const handleSave = async () => {
    setSubmitted(true);
    if (inFlight) return;
    // 10-27 使用者裁決（取代 AGUI-1283 的儲存時收進）：寄件者、關鍵字框裡還有沒按「加入」的字就不送出，
    // 各自在框下方說明，兩個框都有字時同時顯示，所以兩個都要檢查、不能短路
    const senderPending = senderPendingRef.current?.() || false;
    const keyword = keywordText.trim();
    if (keyword) {
      setKeywordError(draft.subjectKeywords.includes(keyword)
        ? `「${keyword}」已在清單中`
        : '還有關鍵字未加入，請按【加入】或清除後再儲存');
      // 「過濾規則」收起時關鍵字框不在畫面上，展開才看得到紅字（10-27 使用者裁決 A）
      setFilterOpen(true);
    }
    // 有問題就不送出，畫面捲到最上面那個有問題的欄位（AGUI-1256：表單可能很長，紅字在畫面外看不到）。
    // 10-27 使用者裁決：只捲動、游標不動，必填沒填也不再把游標放進欄位（免得手機跳出鍵盤）。每次按都捲，所以每次給新的物件
    const problemId = nameMissing ? 'mail-rule-name'
      : senderPending ? 'mail-rule-from'
        : keyword ? 'mail-rule-keyword'
          : senderMissing ? 'mail-rule-from' : null;
    if (problemId) {
      // AGUI-1346：手機上游標若還在下方另一個輸入框（「儲存」按下時不搶焦點），鍵盤開著、畫面會被拉回那個框，
      // 捲不到上面的錯誤欄位。要捲到別的欄位時先讓它失焦、收起鍵盤；游標本來就在該欄位則不動
      const active = document.activeElement;
      if (isMobile && active?.closest?.('.mail-rule-form') && active.id !== problemId) active.blur();
      setScrollTarget({ id: problemId });
      return;
    }
    // 寄件者必填，有寄件者就一定有條件，不必另外檢查
    // AGUI-1314：真的要送出了才讓輸入框失焦、收起手機鍵盤——AGUI-1283 為了點擊不落空，按下「儲存」時游標還留在
    // 剛被清空的寄件者框，使用者會以為沒輸入到又開始重打
    const active = document.activeElement;
    if (active?.closest?.('.mail-rule-form')) active.blur();
    setSaving(true);
    setError(null);
    const body = {
      ruleName: draft.ruleName,
      fromAddresses: draft.fromAddresses,
      fromDomains: draft.fromDomains,
      subjectKeywords: draft.subjectKeywords,
      importance: draft.importance || null,
      // 附件三態原樣送：true 有附件、false 沒有附件、null 不限
      hasAttachment: draft.hasAttachment,
      aiPrompt: draft.aiPrompt,
    };
    const result = await submit(body, { onPending: () => setProcessing(true) });
    if (result.ok) {
      onSaved(result.data);
      return;
    }
    // 失敗與逾時都保留已填內容；逾時沒有錯誤可講，狀態卡副標題的「連線逾時…」已經說明
    setSaving(false);
    setProcessing(false);
    if (result.error) setError(result.error);
  };

  return (
    <form
      ref={formRef}
      className="mail-rule-form mail-rule-form--inline"
      aria-label={title}
      onSubmit={(e) => e.preventDefault()}
    >
      <div className="mail-rule-form__header">
        <h3 className="mail-rule-form__title">{title}</h3>
        <button type="button" className="mail-rule-form__close" aria-label="關閉新增規則" onClick={onCancel}>
          ✕
        </button>
      </div>

      <div className="mail-rule-form__field">
        {/* 必填星號用 ::before 畫在名稱前面，不進 label 文字，螢幕閱讀器改靠 aria-required 得知。
            AGUI-1266：不用原生 required——瀏覽器會在滑過空白輸入框時跳出「請填寫這個欄位。」提示，必填改由紅框與紅字說明 */}
        <label htmlFor="mail-rule-name" className="mail-rule-form__label--required">規則名稱</label>
        <MailRuleClearInput label="規則名稱" value={draft.ruleName} inputRef={nameRef}
          onClear={() => set('ruleName')('')}>
          <input
            id="mail-rule-name"
            ref={nameRef}
            className={nameInvalid ? 'mail-rule-form__input--invalid' : undefined}
            maxLength={50}
            aria-required="true"
            aria-invalid={nameInvalid}
            aria-describedby={nameInvalid ? 'mail-rule-name-error' : undefined}
            value={draft.ruleName}
            onChange={(e) => set('ruleName')(clip(e.target.value, 50))}
          />
        </MailRuleClearInput>
        {nameInvalid && (
          // 10-22 使用者裁決：紅框下方恢復顯示紅字（推翻 10-21「只留紅框」，b667c24）
          <div id="mail-rule-name-error" className="mail-rule-form__field-error" role="alert">
            請輸入規則名稱
          </div>
        )}
      </div>

      {/* 寄件者必填，緊接在規則名稱下方、不放進選填的「過濾規則」群組（10-05 使用者裁決）。
          信箱與網域合成一張清單（10-05），符合任一位即可。按過儲存仍沒加時以紅字說明 */}
      {/* AGUI-1264：標題由「寄件者條件」改為「寄件者」 */}
      <FormGroup id="mail-rule-sender" title="寄件者" required plain>
        {/* 「請至少加入一位寄件者」比照規則名稱，按過「儲存」才出現；由清單元件顯示，
            輸入框裡有格式錯誤時只講格式錯誤（AGUI-1264） */}
        <MailRuleSenderList id="mail-rule-from" addresses={draft.fromAddresses} domains={draft.fromDomains}
          initialMode={senderMode}
          onChange={(next) => setDraft((d) => ({ ...d, ...next }))}
          pendingRef={senderPendingRef}
          invalid={senderInvalid}
          describedBy="mail-rule-sender-error"
          invalidMessage="請至少加入一位寄件者" />
      </FormGroup>

      <OptionalGroup id="mail-rule-filter" title="過濾規則"
        desc="主旨關鍵字、重要性、附件" filled={filterFilled(draft)}
        open={filterOpen} onToggle={toggleGroup(filterOpen, setFilterOpen, 'mail-rule-filter-group-head')}>
        <KeywordList id="mail-rule-keyword" label="信件主旨包含特定關鍵字" values={draft.subjectKeywords}
          onChange={set('subjectKeywords')}
          text={keywordText} onTextChange={setKeywordText} error={keywordError} onErrorChange={setKeywordError}
          placeholder="輸入關鍵字，例如 急件" max={20} maxLength={50}
          hint="關鍵字比對不區分大小寫與全半形且忽略空格與符號" />
        <ClearableSelect id="mail-rule-importance" label="重要性" value={draft.importance}
          onChange={set('importance')}>
          <option value="">不限</option>
          <option value="high">高</option>
          <option value="normal">一般</option>
          <option value="low">低</option>
        </ClearableSelect>
        {/* 附件三態：不限（null）／有附件（true）／沒有附件（false） */}
        <ClearableSelect id="mail-rule-attachment" label="包含附件" value={ATTACHMENT_VALUE[draft.hasAttachment] || ''}
          onChange={(v) => set('hasAttachment')(fromAttachmentValue(v))}>
          <option value="">不限</option>
          <option value="yes">有附件</option>
          <option value="no">沒有附件</option>
        </ClearableSelect>
        {/* 「我在收件者欄」已移除（使用者裁決）；後端仍接受 toMe，既有規則的複述照常顯示 */}
      </OptionalGroup>

      {/* 標題本身已含 AI，不再加 AI 徽章（同範本卡的作法）；10-19 PO 檢視拿掉「交給 AI 判斷是否通知」說明 */}
      <OptionalGroup id="mail-rule-ai-group" title="AI 判讀信件" className="mail-rule-form__group--ai"
        filled={draft.aiPrompt.trim() ? `「${draft.aiPrompt.trim()}」` : ''}
        open={aiOpen} onToggle={toggleGroup(aiOpen, setAiOpen, 'mail-rule-ai-group-group-head')}>
        <label htmlFor="mail-rule-ai" className="mail-rule-form__sr-only">AI 描述</label>
        {/* AGUI-1284（10-22 補）：AI 判讀描述輸入後也要有 ✕ */}
        <MailRuleClearInput label="AI 描述" value={draft.aiPrompt} inputRef={aiRef} multiline
          onClear={() => set('aiPrompt')('')}>
          <textarea id="mail-rule-ai" ref={aiRef} maxLength={AI_MAX} value={draft.aiPrompt}
            onChange={(e) => set('aiPrompt')(clip(e.target.value, AI_MAX))} />
        </MailRuleClearInput>
        <div className="mail-rule-form__counter">{draft.aiPrompt.length} / {AI_MAX}</div>
        <div className="mail-rule-form__hint">MyBro 助理小哥是 AI，有時可能會出錯。</div>
        {/* AI 判讀上限由 MyAgent／Kestra 決定，確認前不寫數字（A-09、NQ-02）；舊版的每日 50 筆說明已隨 AGUI-1246 移除 */}
      </OptionalGroup>

      {/* 條件複述：標題「【{規則名稱}】規則」，還沒填名稱時為「這筆規則」（10-19 PO 檢視）。
          最後一行固定是通知方式，前面空一行。
          還沒加寄件者時規則不成立，不列通知方式；不另外提示（必填由寄件者輸入框的紅框與錯誤訊息負責） */}
      <div className="mail-rule-form__summary" aria-live="polite">
        <div className="mail-rule-form__summary-title">
          {draft.ruleName.trim() ? `【${draft.ruleName.trim()}】規則` : '這筆規則'}
        </div>
        {conditionLines.map((line) => <div key={line}>{line}</div>)}
        {!senderMissing && <div className="mail-rule-form__summary-notify">{NOTIFY_LINE}</div>}
      </div>

      <MailNotifyErrorNotice error={error} isMobile={isMobile} />

      <div className="mail-rule-form__buttons">
        {/* AGUI-1283：按下時不讓輸入框失焦。離開輸入框已不加入（D-1022-06），保留是為了框裡有字被擋下時游標不動（10-27） */}
        <button type="button" className="connector-pill connector-pill--connect" disabled={inFlight}
          onMouseDown={(e) => e.preventDefault()}
          onClick={handleSave}>
          {processing ? '處理中...' : saving ? '儲存中...' : '儲存'}
        </button>
      </div>
    </form>
  );
}
